import { ChordType, Interval } from "../../wasm/pkg/wasm";
import { NoteEditMeta } from "../components/music_controls/custom_buttons/ChordEditNoteRow";

function pickSus2ChordType(sounded: Array<Interval>): ChordType | undefined {
  // 1, 2, + whatever the highest
  switch (sounded[sounded.length - 1]) {
    case "PerfectFifth":
      return "TriadSus2";
    case "MajorSixth":
      return "SixthSus2";
    case "MinorSeventh":
      return "MinorSeventhSus2";
    case "MajorSeventh":
      return "MajorSeventhSus2";
    default:
      return undefined;
  }
}

function pickSus4ChordType(sounded: Array<Interval>): ChordType | undefined {
  // 1, 4, + whatever the highest
  switch (sounded[sounded.length - 1]) {
    case "PerfectFifth":
      return "TriadSus4";
    case "MajorSixth":
      return "SixthSus4";
    case "MinorSeventh":
      return "MinorSeventhSus4";
    case "MajorSeventh":
      return "MajorSeventhSus4";
    default:
      return undefined;
  }
}

function pick5ChordType(sounded: Array<Interval>): ChordType | undefined {
  const hasMin7 = sounded.find((i) => i === "MinorSeventh");
  const hasMaj7 = sounded.find((i) => i === "MajorSeventh");
  switch (sounded[sounded.length - 1]) {
    case "PerfectFifth":
      return "Five";
    case "PerfectEleventh":
      // without a minor 3rd we really don't know Dom vs. Minor
      if (hasMin7) return "DominantEleventh";
      if (hasMaj7) return "MajorEleventh";
      return undefined;
    default:
      return undefined;
  }
}

function pickMin3ChordType(sounded: Array<Interval>, silent: Array<Interval>): ChordType | undefined {
  // 1, m3, (implied or real flat5), + whatever the highest
  const hasFlat5 = sounded.find((i) => i === "FlatFifth") || silent.find((i) => i === "FlatFifth");

  switch (sounded[sounded.length - 1]) {
    case "FlatFifth":
      return "DiminishedTriad";
    case "PerfectFifth":
      return "MinorTriad";
    case "MajorSixth":
      return hasFlat5 ? "DiminishedSeventh" : "MinorSixth";
    case "MinorSeventh":
      return hasFlat5 ? "HalfDiminishedSeventh" : "MinorSeventh";
    case "MajorSeventh":
      return "MinorMajorSeventh";
    case "MajorNinth":
      return "MinorNinth";
    case "PerfectEleventh":
      return "MinorEleventh";
    case "MinorThirteenth":
      return "MinorThirteenth";
    default:
      return undefined;
  }
}

function pickMaj3ChordType(sounded: Array<Interval>, silent: Array<Interval>): ChordType | undefined {
  // 1, major 3, (implied or real, maj vs. min 7th) + whatever the highest note is
  const hasMaj7 = sounded.find((i) => i === "MajorSeventh") || silent.find((i) => i === "MajorSeventh");
  switch (sounded[sounded.length - 1]) {
    case "PerfectFifth":
      return "MajorTriad";
    case "MajorSixth":
      return "MajorSixth";
    case "MinorSeventh":
      return "DominantSeventh";
    case "MajorSeventh":
      return "MajorSeventh";
    case "MajorNinth":
      return hasMaj7 ? "MajorNinth" : "DominantNinth";
    case "PerfectEleventh":
      return hasMaj7 ? "MajorEleventh" : "DominantEleventh";
    case "MinorThirteenth":
      return "DominantMinThirteenth";
    case "MajorThirteenth":
      return hasMaj7 ? "MajorThirteenth" : "DominantMajThirteenth";
    case "MinorNinth":
      return "AlteredFlatNinth";
    default:
      return undefined;
  }
}

// Only 11th chords are likely to have the 3rd and 5th silenced
function pickMin7ShellChordType(sounded: Array<Interval>, silent: Array<Interval>) {
  const impliedMaj3 = silent.find((i) => i === "MajorThird");
  switch (sounded[sounded.length - 1]) {
    case "PerfectEleventh":
      return impliedMaj3 ? "DominantEleventh" : "MinorEleventh";

    default:
      return undefined;
  }
}
function pickMaj7ShellChordType(sounded: Array<Interval>) {
  switch (sounded[sounded.length - 1]) {
    case "PerfectEleventh":
      return "MajorEleventh";
    default:
      return undefined;
  }
}

/**
 * Tree of conditions for "what is this chord" after subtractions, given the subset defined in rust.
 *
 * Undefined -> It's not really a chord anymore, but go ahead.
 */
export function buildChordTypeFromSortedChordIntervals(notes: Array<NoteEditMeta>): ChordType | undefined {
  const sounded = notes.filter((n) => n.isSounded).map((n) => n.note.interval.chord_int);
  const silent = notes.filter((n) => !n.isSounded).map((n) => n.note.interval.chord_int);
  if (sounded[0] !== "Tonic") {
    return undefined;
  }
  switch (sounded[1]) {
    case "MajorSecond": {
      return pickSus2ChordType(sounded);
    }
    case "PerfectFourth": {
      return pickSus4ChordType(sounded);
    }
    case "MinorThird": {
      return pickMin3ChordType(sounded, silent);
    }
    case "MajorThird": {
      return pickMaj3ChordType(sounded, silent);
    }
    case "PerfectFifth": {
      return pick5ChordType(sounded);
    }
    case "MinorSeventh": {
      return pickMin7ShellChordType(sounded, silent);
    }
    case "MajorSeventh": {
      return pickMaj7ShellChordType(sounded);
    }
    default:
      return undefined;
  }
}
